import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { Pill, StatTile } from '../../ui/DataBits';
import { useI18n } from '../../i18n';
import { AdminFrame } from './AdminFrame';
import { AttentionItem, errorText, Overview, rpc, SchoolRow } from './api';
import { SchoolPills, SetupProgress } from './pills';
import type { Strings } from '../../i18n/strings';

type Attn = { key: string; text: string; to: string; action: string };

// Platform admin landing page: a summary, not a form. What's on the
// platform, what needs Zencera's attention (each with its own link), and one
// click to each common task.
export function AdminOverview() {
  const { t } = useI18n();
  return (
    <AdminFrame section="overview" title={t['overview.title']}>
      {({ ctx }) => <OverviewBody name={ctx.display_name} />}
    </AdminFrame>
  );
}

function attentionLine(t: Strings, a: AttentionItem): Attn {
  switch (a.kind) {
    case 'paperwork':
      return {
        key: `pw-${a.school_id}`,
        text: t['overview.attnPaperwork'](a.school_name),
        to: `/admin/schools/${a.school_id}`,
        action: t['overview.attnView'],
      };
    case 'setup':
      return {
        key: `setup-${a.school_id}`,
        text: t['pa.attnSetup'](a.school_name, a.done, a.total),
        to: `/admin/schools/${a.school_id}`,
        action: t['pa.attnSeeChecklist'],
      };
    case 'offboard_ready':
      return {
        key: `off-${a.school_id}`,
        text: a.ack ? t['pa.attnOffboardReady'](a.school_name) : t['pa.attnOffboardAck'](a.school_name),
        to: `/admin/schools/${a.school_id}`,
        action: t['overview.attnView'],
      };
    case 'no_staff':
      return {
        key: `staff-${a.school_id}`,
        text: t['overview.attnNoStaff'](a.school_name),
        to: `/admin/schools/${a.school_id}/staff`,
        action: t['overview.attnAddStaff'],
      };
    case 'no_school_admin':
      return {
        key: `sa-${a.school_id}`,
        text: t['pa.attnNoSchoolAdmin'](a.school_name),
        to: `/admin/schools/${a.school_id}/staff`,
        action: t['overview.attnView'],
      };
    case 'one_platform_admin':
      return { key: 'pa1', text: t['pa.attnOneAdmin'], to: '/admin/settings', action: t['overview.attnView'] };
  }
}

function OverviewBody({ name }: { name: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<Overview | null>(null);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mfaCount, setMfaCount] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([rpc<Overview>('platform_overview'), rpc<SchoolRow[]>('platform_list_schools')]).then(([o, s]) => {
      if (o.error) setError(errorText(t, o.error));
      else setData(o.data);
      if (s.data) setSchools(s.data);
    });
    supabase.auth.mfa
      .listFactors()
      .then(({ data: f }) => setMfaCount(f?.totp?.length ?? 0))
      .catch(() => setMfaCount(null));
  }, [t]);

  if (error) {
    return (
      <p className="error-text" role="alert">
        {error}
      </p>
    );
  }
  if (!data) return <p role="status" className="text-muted">{t.loading}</p>;

  const attention: Attn[] = [];
  if (mfaCount === 0) attention.push({ key: 'mfa', text: t['overview.attnMfa'], to: '/mfa', action: t['overview.attnMfaAction'] });
  else if (mfaCount === 1)
    attention.push({ key: 'mfa-b', text: t['overview.attnMfaBackup'], to: '/mfa', action: t['overview.attnMfaBackupAction'] });
  if (data.approvals > 0)
    attention.push({ key: 'appr', text: t['pa.attnApprovals'](data.approvals), to: '/admin/approvals', action: t['pa.attnReview'] });
  if (data.tests_expired > 0)
    attention.push({ key: 'tx', text: t['pa.attnTestsExpired'](data.tests_expired), to: '/admin/settings', action: t['overview.attnView'] });
  for (const a of data.attention) attention.push(attentionLine(t, a));

  const recent = schools
    .filter((s) => s.state !== 'offboarded')
    .slice()
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 5);

  return (
    <>
      <p className="page-intro">{t['overview.hello'](name)}</p>

      <div className="stat-row">
        <StatTile label={t['stat.schools']} value={data.schools.total} sub={t['overview.schoolsSub'](data.schools.sandbox, data.schools.customer)} />
        <StatTile label={t['pa.statLive']} value={data.schools.live} sub={t['pa.statLiveSub'](data.schools.setting_up, data.schools.suspended)} />
        <StatTile label={t['pa.statApprovals']} value={data.approvals} sub={t['pa.statApprovalsSub']} />
        <StatTile label={t['pa.statTests']} value={data.test_accounts} sub={t['pa.statTestsSub'](data.tests_expiring)} />
      </div>

      <section aria-labelledby="quick-title">
        <h2 className="overline" id="quick-title">
          {t['overview.quick']}
        </h2>
        <ul className="action-grid">
          <li>
            <Link className="action-card" to="/admin/schools/new">
              <span className="action-icon" aria-hidden="true">🏫</span>
              <span className="action-name">{t['overview.addSchool']}</span>
              <span className="action-desc">{t['pa.addSchoolDesc']}</span>
            </Link>
          </li>
          <li>
            <Link className="action-card" to="/admin/approvals">
              <span className="action-icon" aria-hidden="true">✅</span>
              <span className="action-name">{t['pa.navApprovals']}</span>
              <span className="action-desc">{t['pa.approvalsDesc']}</span>
            </Link>
          </li>
          <li>
            <Link className="action-card" to="/admin/log">
              <span className="action-icon" aria-hidden="true">📜</span>
              <span className="action-name">{t['pa.navLog']}</span>
              <span className="action-desc">{t['pa.logDesc']}</span>
            </Link>
          </li>
        </ul>
      </section>

      <div className="two-col two-col-even">
        <section className="panel p5" aria-labelledby="attn-title">
          <h2 className="panel-title" id="attn-title">
            {t['overview.attention']}
          </h2>
          {attention.length === 0 ? (
            <p className="empty-inline">
              <Pill kind="good">{t['overview.allClear']}</Pill> {t['overview.allClearBody']}
            </p>
          ) : (
            <ul className="attn-list">
              {attention.map((a) => (
                <li key={a.key}>
                  <span>{a.text}</span>
                  <Link to={a.to}>{a.action}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel p5" aria-labelledby="recent-title">
          <div className="panel-head">
            <h2 className="panel-title" id="recent-title">
              {t['overview.schools']}
            </h2>
            <Link to="/admin/schools">{t['overview.seeAllSchools']}</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-muted">
              {t['admin.noSchools']} <Link to="/admin/schools/new">{t['overview.addSchool']}</Link>
            </p>
          ) : (
            <ul className="school-list">
              {recent.map((s) => (
                <li key={s.id}>
                  <span className="school-list-name">
                    <Link to={`/admin/schools/${s.id}`}>{s.name}</Link> <span className="code-chip">{s.code}</span>
                  </span>
                  <span className="pill-row">
                    <SchoolPills school={s} />
                  </span>
                  {s.state === 'setting_up' && <SetupProgress done={s.required_done} total={s.required_total} />}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}
